/* eslint-disable react-hooks/exhaustive-deps */
import Combokeys from 'combokeys';
//eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import mixinGlobalBind from 'combokeys/plugins/global-bind';
//eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import mixinPause from 'combokeys/plugins/pause';
import { useEffect } from 'react';
import 'symbol-observable';
import type { ObservableLike, Observer, Unsubscribable } from 'type-fest';

import {
  CacheMap,
  CacheWeakMap,
  nestedCacheMapFactory,
} from '@frontend/datatypes';
import { callNextObservers, subscribeObserver, cssPath } from '@frontend/utils';

type CSSPathString = string;

type MouseKeyBinding = string;
type MouseKeyBindings = string[];
type MouseKeyBindingOrBindings = MouseKeyBinding | MouseKeyBindings; //Parameters<typeof MouseTrap['bind']>[0]; //First argument of MouseTrap.bind is `string | string[]`;

type MixinPause = (comboKeys: Combokeys.Combokeys) => Combokeys.Combokeys & {
  pause(): void;
  unpause(): void;
};

type CombokeysWithPause = ReturnType<MixinPause>;

type MixinGlobalBind = (comboKeys: CombokeysWithPause) => CombokeysWithPause & {
  bindGlobal(
    keys: string | string[],
    callback: (event: KeyboardEvent) => void,
    action?: string,
  ): void;
};

type CombokeysWithPauseAndGlobalBind = ReturnType<MixinGlobalBind>;

type MouseKeyOptions = {
  context?: Element;
  //if true, will also include bindings to elements such as input, textArea (by default it is falsey)
  //* In otherwords, comboKeys.bindGlobal() is used instead of comboKeys.bind(). So if the user is inside a input element, the binding is listened to.
  //  This is useful for binding a key such as `esc` which we want to listen to inside an input element. We don't want to listen to other key presses like
  //  '/' inside an input element because the standard behavior of receiving the key in an input element is desired.
  isBoundToAllElementTypes?: boolean;
  keyEventType?: KeyEventType;
};

export type MouseKeyBindingEvent = KeyboardEvent;

type MouseKeyBindingObserver = Partial<Observer<MouseKeyBindingEvent>>;

/*
 * ObservableMouseKeyBinding is an observable to keyboard bindings created by the mouseKeyBindingService
 * @remarks `ObservableMouseKeyBinding` is not exported. It should be constructed using `mouseKeyBindingService`
 */
class ObservableMouseKeyBinding
  implements ObservableLike<MouseKeyBindingEvent>
{
  private subscriptions = new Set<MouseKeyBindingObserver>();
  private handler?: (mouseKeyEvent: MouseKeyBindingEvent) => void;
  private isPaused?: boolean;

  constructor(
    private readonly mouseKeyBindingOrBindings: MouseKeyBindingOrBindings,
    public readonly comboKeys: CombokeysWithPauseAndGlobalBind,
    public readonly isBoundToAllElementTypes: boolean,
    public readonly keyEventType: KeyEventType,
    private deleteFromParent: () => void,
  ) {
    this.isBoundToAllElementTypes = isBoundToAllElementTypes;
    const subscriptions = this.subscriptions;
    const isActive = () => !this.isPaused;
    this.handler = (mouseKeyEvent: MouseKeyBindingEvent): void => {
      if (isActive()) {
        callNextObservers(subscriptions, mouseKeyEvent);
      }
    };

    if (isBoundToAllElementTypes) {
      if (keyEventType === 'keypress') {
        this.comboKeys.bindGlobal(mouseKeyBindingOrBindings, this.handler);
      } else {
        this.comboKeys.bindGlobal(
          mouseKeyBindingOrBindings,
          this.handler,
          keyEventType,
        );
      }
    } else {
      if (keyEventType === 'keypress') {
        this.comboKeys.bind(mouseKeyBindingOrBindings, this.handler);
      } else {
        this.comboKeys.bind(
          mouseKeyBindingOrBindings,
          this.handler,
          keyEventType,
        );
      }
    }
  }

  /*
   * Returns true if this ObservableMouseKeyBinding has been disposed, otherwise false
   */
  public get isDisposed(): boolean {
    return !this.handler;
  }

  /*
   * Disposes of the bindings and its observers
   */
  public dispose(): void {
    if (!this.isDisposed) {
      for (const mouseKeyBinding of this.bindings) {
        this.comboKeys.unbind(mouseKeyBinding);
      }
      this.subscriptions.clear();
      this.deleteFromParent();
      delete this.handler;
    }
  }

  /*
   * pauses observation of mouseKeyBindingOrBindings
   */
  public pause() {
    this.isPaused = true;
  }

  /*
   * resumes observation of mouseKeyBindingOrBindings
   */
  public unpause() {
    delete this.isPaused;
  }

  public get hasBindings(): boolean {
    return this.subscriptions.size > 0;
  }

  /*
   * Returns an iterable of the keyboard/mouse bindings being observed
   * @remarks Implemented as Iterable so that mouseKeyBindingOrBindings is not mutated
   */
  public get bindings(): Iterable<MouseKeyBinding> {
    const mouseKeyBindingOrBindings = this.mouseKeyBindingOrBindings;
    return {
      *[Symbol.iterator]() {
        if (Array.isArray(mouseKeyBindingOrBindings)) {
          yield* mouseKeyBindingOrBindings;
        } else {
          yield mouseKeyBindingOrBindings;
        }
      },
    };
  }

  /*
   * subscribe to this observable with an observer
   * @param observer - observer callbacks
   * @see alias bind()
   */
  public subscribe(observer: MouseKeyBindingObserver): Unsubscribable {
    return subscribeObserver(observer, this.subscriptions);
  }

  /*
   * unsubscribe an observer()
   * @param observer - the subscribed observer that should be unsubscribed
   * @see alias unbind()
   */
  public unsubscribe(observer: MouseKeyBindingObserver): boolean {
    const result = this.subscriptions.delete(observer);
    return result;
  }

  /*
   * [Symbol.observable](): Required for ObservableLike<MouseKeyBindingEvent> interface
   */
  public [Symbol.observable](): this {
    return this;
  }
}

//Define aliases: Each alias added to interface and prototype
interface ObservableMouseKeyBinding {
  /*
   * bind an observer to this observable
   * @param observer - observer callbacks
   * @see alias subscribe()
   */
  bind: typeof ObservableMouseKeyBinding.prototype.subscribe;

  /*
   * unbind an observer()
   * @param observer - the subscribed observer that should be unbound
   * @see alias unsubscribe()
   */
  unbind: typeof ObservableMouseKeyBinding.prototype.unsubscribe;
}
ObservableMouseKeyBinding.prototype.bind =
  ObservableMouseKeyBinding.prototype.subscribe;
ObservableMouseKeyBinding.prototype.unbind =
  ObservableMouseKeyBinding.prototype.unsubscribe;

//cleanWhitespace(): replace whitespace with single space, and remove trailing whitespace
const cleanWhitespace = (input: string): string =>
  input.trim().replace(/\s\s+/g, ' ');

type KeyEventType = 'keypress' | 'keydown' | 'keyup';

/*
 * MouseKeyBindingService is a service for binding hotkeys to an Observable and creating hooks to observe hotkeys being pressed
 * @remarks - The class MouseKeyBindingService is not intended to be exported. It is used to construct the singleton mouseKeyBindingService below; which is exported
 */
class MouseKeyBindingService {
  /*
   * Private lookup up of Combokeys by Element
   */
  private contexts = new Map<Element, CombokeysWithPauseAndGlobalBind>();

  /*
   * observableLookup is a nested map to lookup ObservableMouseKeyBinding by MouseKeyBinding, KeyEventType, and Element
   * @remarks - It uses nestedCacheMapFactory()/CacheMap to make it easier to create inner values and dispose of key/value pairs as needed
   * @see - reverseLookup which is the inverse lookup by ObservableMouseKeyBinding
   */
  private observableLookup: CacheMap<
    Element,
    CacheMap<KeyEventType, Map<MouseKeyBinding, ObservableMouseKeyBinding>>
  > = nestedCacheMapFactory(
    2, //2 levels of CacheMap (Types of each level are in declaration above)
    //leafValueFactory:
    //* Note: this Map makes it like a 3rd level deep (extra level is necessary because of special dispose method for observableByEncodedBinding)
    () => new Map<MouseKeyBinding, ObservableMouseKeyBinding>(),
    //leafDisposeValue:
    (
      observableByEncodedBinding: Map<
        MouseKeyBinding,
        ObservableMouseKeyBinding
      >,
    ) => {
      for (const binding of observableByEncodedBinding.values()) {
        binding.dispose();
      }
      observableByEncodedBinding.clear();
    },
  );

  /*
   * reverseLookup provides means to lookup related ObservableMouseKeyBindings
   * @remarks - It uses CacheMap to make it easier to create inner values and dispose of key/value pairs as needed
   */
  private reverseLookup: CacheWeakMap<
    ObservableMouseKeyBinding,
    Set<MouseKeyBinding>
  > = new CacheWeakMap(
    () => new Set(),
    (v) => v.clear(),
  );

  /*
   * Remove all bindings registered with service
   */
  public clear() {
    this.observableLookup.clear();
    for (const comboKeys of this.contexts.values()) {
      comboKeys.reset();
    }
    this.contexts.clear();
  }

  /*
   * An iterable of the `MouseKeyBindings` registered with service
   */
  public get bindings(): Iterable<
    [CSSPathString, KeyEventType, MouseKeyBinding]
  > {
    const observableLookup = this.observableLookup;
    return {
      *[Symbol.iterator]() {
        for (const [
          context,
          keyEventTypeLookup,
        ] of observableLookup.entries()) {
          const path: CSSPathString = cssPath(context) || 'documentElement';
          for (const [
            keyEventType,
            bindingLookup,
          ] of keyEventTypeLookup.entries()) {
            for (const [
              mouseKeyBinding,
              observableBinding,
            ] of bindingLookup.entries()) {
              if (observableBinding.isDisposed) {
                bindingLookup.delete(mouseKeyBinding); //should have been removed already
              } else {
                yield [path, keyEventType, mouseKeyBinding];
              }
            }
          }
        }
      },
    };
  }

  /*
   * bind(): Binds and ObservableMouseKeyBinding to the `mouseKeyBindingOrBindings`
   * @param mouseKeyBindingOrBindings - See binding syntax in https://craig.is/killing/mice
   * @param isBoundToAllElementTypes - if true, then there will be observations inside input elements.
   * @remarks This method is a factory for a new ObservableMouseKeyBinding if the binding does not exist or
   * getter an existing ObservableMouseKeyBinding if one exists
   */
  public bind(
    mouseKeyBindingOrBindings: MouseKeyBindingOrBindings,
    options?: MouseKeyOptions,
  ): ObservableMouseKeyBinding {
    const context = options?.context ?? document.documentElement;
    const isBoundToAllElementTypes = !!options?.isBoundToAllElementTypes;
    const keyEventType: KeyEventType = options?.keyEventType ?? 'keypress';

    const _mouseKeyBindingOrBindings: MouseKeyBindingOrBindings =
      typeof mouseKeyBindingOrBindings === 'string'
        ? cleanWhitespace(mouseKeyBindingOrBindings)
        : mouseKeyBindingOrBindings.map(cleanWhitespace).sort();
    const __mouseKeyBindingOrBindings: MouseKeyBindings =
      typeof _mouseKeyBindingOrBindings === 'string'
        ? [_mouseKeyBindingOrBindings]
        : _mouseKeyBindingOrBindings;

    //Attempt to lookup existing observableBinding.
    //* Verify that if there is an existing observableBinding, that every binding in __mouseKeyBindingOrBindings is consistent
    const observableLookup = this.observableLookup
      .get(context)
      .get(keyEventType);
    let observableBinding: ObservableMouseKeyBinding | undefined;
    for (const mouseKeyBinding of __mouseKeyBindingOrBindings) {
      const _observableBinding = observableLookup.get(mouseKeyBinding);

      /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
      if (observableBinding && _observableBinding !== observableBinding) {
        const conflicting = (
          Array.isArray(_mouseKeyBindingOrBindings)
            ? _mouseKeyBindingOrBindings.filter((s) => s !== mouseKeyBinding)
            : []
        ).join(', ');
        throw new Error(
          `Unexpected binding for [${cssPath(
            context,
          )} ${keyEventType} ${mouseKeyBinding}]. A different binding exists that conflicts with ${conflicting}`,
        );
      }
      if (_observableBinding) {
        const existingBindingIsGlobal =
          !!_observableBinding.isBoundToAllElementTypes;
        if (existingBindingIsGlobal) {
          if (!isBoundToAllElementTypes) {
            throw new Error(
              'Unexpected binding. Existing binding is global, whereas this binding is not',
            );
          }
        } else if (isBoundToAllElementTypes) {
          throw new Error(
            'Unexpected binding. This binding is global, but there is an existing binding that is not global.',
          );
        }
        observableBinding = _observableBinding;
      }
    }
    //If binding does not exist, create it:
    if (!observableBinding) {
      let comboKeys = this.contexts.get(context);
      if (!comboKeys) {
        //create it and add it to cache
        comboKeys = (mixinGlobalBind as MixinGlobalBind)(
          (mixinPause as MixinPause)(new Combokeys(context)),
        );
        this.contexts.set(context, comboKeys);
      }

      observableBinding = new ObservableMouseKeyBinding(
        mouseKeyBindingOrBindings,
        comboKeys,
        isBoundToAllElementTypes,
        keyEventType,
        //deleteFromParent
        () => {
          for (const mouseKeyBinding of __mouseKeyBindingOrBindings) {
            observableLookup.delete(mouseKeyBinding);
          }
          if (!observableLookup.size) {
            this.observableLookup.get(context)?.delete(keyEventType);
          }
          this.reverseLookup.delete(
            observableBinding as ObservableMouseKeyBinding,
          );
        },
      );
      if (process?.env?.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(
          `Created binding: ${cssPath(context)}, ${keyEventType}, ${
            Array.isArray(mouseKeyBindingOrBindings)
              ? mouseKeyBindingOrBindings.join(',')
              : mouseKeyBindingOrBindings
          }`,
        );
      }
      for (const mouseKeyBinding of __mouseKeyBindingOrBindings) {
        if (!observableLookup.has(mouseKeyBinding)) {
          observableLookup.set(mouseKeyBinding, observableBinding);
          const bindings = this.reverseLookup.get(observableBinding);
          bindings.add(mouseKeyBinding);
        }
      }
    }
    return observableBinding;
  }

  /*
   * unbind(): removes the binding to `mouseKeyBindingOrBindings`
   * @param mouseKeyBindingOrBindings - See binding syntax in https://craig.is/killing/mice
   * @see alias delete()
   */
  public unbind(
    mouseKeyBindingOrBindings: MouseKeyBindingOrBindings,
    options?: Pick<MouseKeyOptions, 'context' | 'keyEventType'>,
  ): boolean {
    const context = options?.context ?? document.documentElement;
    let deleted = false;
    if (!this.observableLookup.has(context)) {
      return deleted;
    }
    const observableLookupByContext = this.observableLookup.get(context);
    const keyEventType: KeyEventType = options?.keyEventType ?? 'keypress';
    if (!observableLookupByContext.has(keyEventType)) {
      return deleted;
    }
    const observableLookupByKeyType =
      observableLookupByContext.get(keyEventType);
    for (const _mouseKeyBinding of typeof mouseKeyBindingOrBindings === 'string'
      ? [mouseKeyBindingOrBindings]
      : mouseKeyBindingOrBindings) {
      const mouseKeyBinding = cleanWhitespace(_mouseKeyBinding);
      if (mouseKeyBinding) {
        const observableBinding =
          observableLookupByKeyType.get(mouseKeyBinding);
        if (observableBinding) {
          //dispose/delete from observableLookupByKeyType (the delete method will take care of deleting ancesstors(s) if needed)
          observableBinding.dispose();
          observableLookupByKeyType.delete(mouseKeyBinding);

          if (this.reverseLookup.has(observableBinding)) {
            const sharedBindings = this.reverseLookup.get(observableBinding);
            for (const sharedBinding of sharedBindings) {
              observableLookupByKeyType.delete(sharedBinding);
            }
            this.reverseLookup.delete(observableBinding);
          }
          if (!observableLookupByKeyType.size) {
            observableLookupByContext.delete(keyEventType);
            if (!observableLookupByContext.size) {
              this.observableLookup.delete(context);
            }
          }
          deleted = true;
          if (process?.env?.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log(
              `Removed binding: ${cssPath(context)}, ${keyEventType}, ${
                Array.isArray(mouseKeyBindingOrBindings)
                  ? mouseKeyBindingOrBindings.join(',')
                  : mouseKeyBindingOrBindings
              }`,
            );
          }
        }
      }
    }
    return deleted;
  }

  /*
   * useMouseKeyBind(): A hook to observe mouseKeyBindingOrBindings
   * @param mouseKeyBindingOrBindings - See binding syntax in https://craig.is/killing/mice
   * @param options -
   * - next - this callback is called each time the key binding is received
   * - error? - this callback is called each time there is an error running each observer's next() when a keybinding is received
   * - rebindDeps? - the remindDeps for useEffect (default is [])
   * - context? - the element to bind to. (Default is document.documentElement and its descendents)
   * - isBoundToAllElementTypes? - if true, binding will apply to all descendent input and textArea elements too.
   * - keyEventType? - 'keypress' | 'keydown' | 'keyup' (default is keypress)
   */
  public useMouseKeyBind(
    mouseKeyBindingOrBindings: MouseKeyBindingOrBindings,
    {
      next,
      error,
      rebindDeps = [],
      ...bindOptions
    }: {
      next: (mouseKeyEvent: MouseKeyBindingEvent) => void;
      error?: (err: unknown) => void;
      rebindDeps?: Parameters<typeof useEffect>[1];
    } & Partial<MouseKeyOptions>,
  ): void {
    const observableBinding = this.bind(mouseKeyBindingOrBindings, bindOptions);
    /**
     * The react-hooks checks assume the class is react component; it is not.
     * useFileProcessingState is a service method that is intended to be only called from a React function component
     * or a custom React Hook function.
     * If useFileProcessingState is called from the correct context, useState() is fine here.
     */
    //eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/rules-of-hooks
    useEffect(() => {
      const unsubscribe = observableBinding.subscribe({
        next,
        ...(error && { error }),
      }).unsubscribe;
      return () => {
        unsubscribe();
      };
    }, rebindDeps);
  }

  /*
   * useEscKeyBind(): A hook to observe the esc key in a particular context element
   * @param options -
   * - next - this callback is called each time the key binding is received
   * - error? - this callback is called each time there is an error running each observer's next() when a keybinding is received
   * - rebindDeps? - the remindDeps for useEffect (default is [])
   * - context? - the element to bind to. (Default is document.documentElement and its descendents)
   * - isBoundToAllElementTypes? - if true, binding will apply to all descendent input and textArea elements too.
   * - keyEventType? - 'keypress' | 'keydown' | 'keyup' (default is keypress)
   */
  public useEscKeyBind({
    next,
    error,
    context,
    rebindDeps = [],
    isBoundToAllElementTypes,
    keyEventType,
  }: {
    next: (mouseKeyEvent: MouseKeyBindingEvent) => void;
    context: Element;
    error?: (err: unknown) => void;
    rebindDeps?: Parameters<typeof useEffect>[1];
  } & Partial<
    Pick<MouseKeyOptions, 'isBoundToAllElementTypes' | 'keyEventType'>
  >): void {
    /**
     * The react-hooks checks assume the class is react component; it is not.
     * useFileProcessingState is a service method that is intended to be only called from a React function component
     * or a custom React Hook function.
     * If useFileProcessingState is called from the correct context, useState() is fine here.
     */
    //eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/rules-of-hooks
    useEffect(() => {
      if (context) {
        const unsubscribe = this.bind('esc', {
          context,
          isBoundToAllElementTypes,
          keyEventType,
        }).subscribe({
          next,
          ...(error && { error }),
        }).unsubscribe;
        return () => {
          unsubscribe();
        };
      }
    }, [context, ...rebindDeps]);
  }

  /*
   * pauses observing key bindings for context
   * @param context - the context to pause observing key bindings for
   */
  public pause(context: Element = document.documentElement) {
    const combokeys = this.contexts.get(context);
    if (combokeys) {
      combokeys.pause();
    }
  }

  /*
   * resumes observing key bindings for context
   * @param context - the context to resume observing key bindings for
   */
  public unpause(context: Element = document.documentElement) {
    const combokeys = this.contexts.get(context);
    if (combokeys) {
      combokeys.unpause();
    }
  }

  public hasBindings(b: ObservableMouseKeyBinding) {
    return this.reverseLookup.has(b);
  }
}

//Define aliases: Each alias added to interface and prototype
interface MouseKeyBindingService {
  /*
   * delete(): removes the binding to `mouseKeyBindingOrBindings`
   * @see alias unbind()
   */
  delete: typeof MouseKeyBindingService.prototype.unbind;
}
MouseKeyBindingService.prototype.delete =
  MouseKeyBindingService.prototype.unbind;

//mouseBindingService is a singleton
export const mouseKeyBindingService = new MouseKeyBindingService();
